/**
 * SD2 (#1465): one rule for a hub answer that is NO VERDICT. The source did not answer, or answered
 * in a way that says nothing about this connection's sign-in or settings, so the answer must never
 * demote a Ready connection or replace what was last learned about it. The runner's classifier
 * (`runner/credentials/classify.ts`, status `unreachable`) and the web's re-validate read this one
 * list, so they agree for every listed hub code.
 */

/** The hub's code for a catalogue query that timed out after the sign-in worked. */
export const CATALOG_TIMEOUT_CODE = "CATALOG_TIMEOUT";

/** Every hub `error.code` that is no verdict (the codes `classify.ts` maps to `unreachable`). */
export const NO_VERDICT_CODES: ReadonlySet<string> = new Set([
  "QUOTA_EXCEEDED",
  "PROBE_TIMEOUT",
  "NETWORK_ERROR",
  "API_ERROR",
  "HOST_UNREACHABLE",
  "CONNECTION_REFUSED",
  "TLS_ERROR",
  "TIMEOUT",
  "UNKNOWN",
  CATALOG_TIMEOUT_CODE,
  // #1888: GA4 and GSC. Skypoint's own Google identity failed, or the API is off on Skypoint's
  // Google Cloud project. Neither is about this connection.
  "IDENTITY_FAILED",
  "API_NOT_ENABLED",
]);

/** True when the hub's code means nothing was learned about the connection. */
export function isNoVerdict(errorCode: string | null | undefined): boolean {
  return typeof errorCode === "string" && NO_VERDICT_CODES.has(errorCode);
}

/** The sentence for a catalogue query that timed out after the sign-in worked. */
export const CATALOG_TIMEOUT_MESSAGE =
  "Signed in, but reading the list of tables did not finish in time. Set a schema filter to read fewer tables, or try again later.";

/**
 * #2439: the sentence for a SQL source whose `TIMEOUT` the hub cannot place. MySQL's validator
 * answers `TIMEOUT` for a connect that never finished and for a table list that ran out of time
 * alike, so the sentence names both and guesses neither.
 */
export const SOURCE_TIMEOUT_MESSAGE =
  "The source did not answer in time. It may be unreachable from skyData, or slow to list its tables. Check that skyData can reach that host and port, or set a schema filter.";

/**
 * #2439 review: the step a network team can act on, the same hand-off as the Snowflake answer
 * (docs/guide/settings.md): the address to allow is skyData's outbound address, which a
 * customer gets from Skypoint.
 */
function networkStep(where: string): string {
  return `ask your network team to allow skyData's outbound address ${where}. Ask Skypoint for that address.`;
}

function portPlace(port: string | null): string {
  return port ? `on port ${port}` : "on the port this connection uses";
}

/**
 * #2439: SQL Server's `TIMEOUT` is the sign-in that never finished (the table list's own timeout
 * is `CATALOG_TIMEOUT`, skypoint-connector-hub `sqlserver/validator.ts`), so nothing was read.
 * Most often the network path is closed, but a wrong server name or port that nothing answers on
 * times out the same way, so the person checks those first. `port` is the connection's port,
 * when the caller has it.
 */
export function sourceUnreachableMessage(port: string | null): string {
  return `skyData could not reach the server. Check the server name and port. If they are right, ${networkStep(portPlace(port))}`;
}

/**
 * #2439: the hub's `HOST_UNREACHABLE`: a server name that did not resolve, and on SQL Server also
 * a named instance whose lookup did not answer. Either a wrong name or a network that does not
 * let skyData look up and reach the server.
 */
export const SOURCE_NOT_FOUND_MESSAGE = `skyData could not find the server. Check the server name. If it is right, ${networkStep("to look it up and reach it")}`;

/**
 * #2439: the hub's `CONNECTION_REFUSED`: a connection refused, reset or dropped. A wrong port, a
 * database that takes no connections, or a firewall that resets the path.
 */
export function sourceRefusedMessage(port: string | null): string {
  const where = port ? ` on port ${port}` : "";
  return `skyData could not open a connection to the server${where}. Check the port number and that the database accepts connections. If they are right, ${networkStep(portPlace(port))}`;
}

/**
 * #2439: the title over a no-verdict answer. Never "Validation failed" (SD2): nothing about the
 * values was judged, and the sentence under it says why.
 */
export const NO_VERDICT_TITLE = "skyData could not check the connection";

/** Said after a no-verdict test of the stored values. */
export const NOTHING_CHANGED_SENTENCE = "Nothing about this connection was changed.";
